import React, { useState, useEffect, useRef } from 'react';
import { db, functions, httpsCallable, doc, onSnapshot } from '../firebase';
import RoastTokenVault from './RoastTokenVault';

const DEFAULT_GIFT_TOKENS = [
    { id: 'spotlight', name: 'Warm Spotlight', price: 500, icon: '🔦' },
    { id: 'popcorn', name: 'Golden Popcorn', price: 1000, icon: '🍿' },
    { id: 'flare', name: 'Rainbow Flare', price: 2500, icon: '🌈' },
    { id: 'chair', name: "Director's Chair", price: 5000, icon: '🎬' },
    { id: 'producer', name: 'The Executive Producer', price: 10000, icon: '💎' },
];

const GlobalTippingModal = () => {
    // --- TRIGGER STATE ---
    const [isOpen, setIsOpen] = useState(false);
    const [targetUser, setTargetUser] = useState(null);
    const [viewerProfile, setViewerProfile] = useState(null);
    const [context, setContext] = useState({}); // { competitionId, entryId, eventId, recipientId }
    
    // --- MODAL INTERNAL STATE ---
    const [giftTokens, setGiftTokens] = useState(DEFAULT_GIFT_TOKENS);
    const [selectedToken, setSelectedToken] = useState(DEFAULT_GIFT_TOKENS[0]);
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [showEarningsConfirm, setShowEarningsConfirm] = useState(false);
    const [isVaultOpen, setIsVaultOpen] = useState(false);

    // 1. Listen for global open event trigger & Hardware Back-Button Trap
useEffect(() => {
    const handleOpen = (e) => {
        const { targetUser, viewerProfile, context } = e.detail || {};
        if (!targetUser || !viewerProfile) return;
        
        setTargetUser(targetUser);
        setViewerProfile(viewerProfile);
        setContext(context || {});
        
        setSelectedToken(giftTokens[0]);
        setIsAnonymous(false);
        setSubmitSuccess(false);
        setIsOpen(true);

        // Trap mobile hardware back button
        window.history.pushState({ modal: 'globalTipping' }, '');
    };

    const handlePopState = () => {
        setIsOpen(false);
        setIsVaultOpen(false);
    };

    window.addEventListener('openGlobalTipping', handleOpen);
    window.addEventListener('popstate', handlePopState);

    return () => {
        window.removeEventListener('openGlobalTipping', handleOpen);
        window.removeEventListener('popstate', handlePopState);
    };
}, [giftTokens]);

    // 2. Dynamic database subscription for Token Economics
    useEffect(() => {
        if (!isOpen) return;
        const unsub = onSnapshot(doc(db, "settings", "tokenEconomics"), (snap) => {
            if (snap.exists() && snap.data().giftTokens) {
                const gTokens = snap.data().giftTokens;
                setGiftTokens(gTokens);
                setSelectedToken(prev => gTokens.find(t => t.id === prev.id) || gTokens[0]);
            }
        });
        return () => unsub();
    }, [isOpen]);

    if (!isOpen || !targetUser || !viewerProfile) return null;

    // Standardized token conversion math (25 GYD = 1 Token)
    const requiredTokens = Math.round((selectedToken?.price || 0) / 25);
    const hasEnoughTokens = (viewerProfile?.tokenBalance || 0) >= requiredTokens;

    const platformFeeGYD = Math.round((selectedToken?.price || 0) * 0.15);
    const actorReceivesGYD = Math.round((selectedToken?.price || 0) * 0.85);

    // --- SECURE CATALOG TIP TRANSACTION TRIGGER ---
    const handleSendCatalogTip = async () => {
        if (!hasEnoughTokens) {
            setIsVaultOpen(true);
            return;
        }
        setIsSubmitting(true);
        try {
            const tipFunc = httpsCallable(functions, 'sendTokenTip');
        await tipFunc({
            targetUserId: targetUser.id,
            giftName: selectedToken?.name || 'Tip',
            amount: requiredTokens,
            competitionId: context.competitionId || null,
            entryId: context.entryId || null,
            eventId: context.eventId || null,
            recipientId: context.recipientId || null,
            source: context.source || (context.competitionId ? 'competition' : 'centerstage'),
            isAnonymous: isAnonymous
        });
            setSubmitSuccess(true);
            setTimeout(() => {
                setSubmitSuccess(false);
                setIsOpen(false);
            }, 3000);
        } catch (err) {
            console.error("Tip failed:", err);
        } finally {
            setIsSubmitting(false);
            setShowEarningsConfirm(false);
        }
    };

    return (
        <>
            <style>{`
                .global-gift-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); display: flex; align-items: center; justify-content: center; z-index: 15000; padding: 16px; }
                .global-gift-modal { background: linear-gradient(180deg, #111111 0%, #050505 100%); border: 1px solid rgba(255,215,0,0.15); border-radius: 24px; width: 100%; max-width: 440px; max-height: 90vh; overflow-y: auto; padding: 32px; box-shadow: 0 30px 60px rgba(0,0,0,0.9), inset 0 1px 1px rgba(255,255,255,0.05); text-align: left; }
                .global-gift-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .global-gift-close { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #FFF; font-size: 18px; cursor: pointer; padding: 0; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 50%; transition: all 0.2s; }
                .global-gift-close:hover { background: #DC3545; border-color: #DC3545; transform: scale(1.05); }
                
                @keyframes floatUpAndFadeScreen {
                    0% { transform: translate(-50%, 0) scale(1); opacity: 1; }
                    100% { transform: translate(-50%, -130px) scale(1.8); opacity: 0; }
                }
                .floating-flame-screen { position: fixed; pointer-events: none; z-index: 99999; font-size: 30px; animation: floatUpAndFadeScreen 1s cubic-bezier(0.25, 1, 0.5, 1) forwards; filter: drop-shadow(0 0 12px #FF4500); }
                
                @keyframes comboPop {
                    0% { transform: scale(0.4) rotate(-12deg); opacity: 0; }
                    50% { transform: scale(1.4) rotate(6deg); opacity: 1; }
                    100% { transform: scale(1) rotate(0deg); opacity: 1; }
                }
                .tiktok-combo-badge { position: absolute; top: -18px; right: -6px; background: linear-gradient(135deg, #FFD700 0%, #FF4500 100%); color: #000; font-size: 11px; font-weight: 900; padding: 2px 7px; border-radius: 12px; box-shadow: 0 0 12px #FF4500; animation: comboPop 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; pointer-events: none; z-index: 100; border: 1px solid #FFF; }
                
                .anon-toggle { display: flex; align-items: center; gap: 10px; margin: 16px 0; cursor: pointer; user-select: none; }
                .anon-toggle input[type="checkbox"] { accent-color: #FF4500; width: 18px; height: 18px; flex-shrink: 0; cursor: pointer; }
                .anon-toggle span { font-size: 12px; color: #AAA; font-weight: 600; }
            `}</style>
            
            <div className="global-gift-overlay" onClick={() => !isSubmitting && setIsOpen(false)}>
                <div className="global-gift-modal" onClick={e => e.stopPropagation()}>
                    {!submitSuccess ? (
                        <>
                            <div className="global-gift-header">
                                <div>
                                    <p style={{ color: '#FFD700', fontSize: '10px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.15em', margin: 0 }}>SUPPORT CREATOR</p>
                                    <h2 style={{ color: '#FFFFFF', fontSize: '24px', fontWeight: 800, margin: '4px 0 0 0' }}>Tip {targetUser.creatorName}</h2>
                                </div>
                                <button className="global-gift-close" onClick={() => setIsOpen(false)}>✕</button>
                            </div>

                            {/* Standardized Wallet Balance */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255, 255, 255, 0.05)', padding: '12px 16px', borderRadius: '12px', marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                <div>
                                    <p style={{ margin: 0, color: '#AAA', fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold' }}>Your Token Balance</p>
                                    <p style={{ margin: '4px 0 0 0', color: '#FFD700', fontSize: '18px', fontWeight: '900', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <svg viewBox="0 0 24 28" width="18" height="22" style={{ overflow: 'visible', filter: 'drop-shadow(0 2px 5px rgba(255,69,0,0.8))' }}>
                                            <defs>
                                                <linearGradient id="flameGradCoinModal" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#FFD700"/>
                                                    <stop offset="50%" stopColor="#FF4500"/>
                                                    <stop offset="100%" stopColor="#8B0000"/>
                                                </linearGradient>
                                            </defs>
                                            <circle cx="12" cy="17" r="8" fill="#B8860B" stroke="#FFD700" strokeWidth="1.2" />
                                            <circle cx="12" cy="17" r="6" fill="none" stroke="#FFE55C" strokeWidth="0.8" strokeDasharray="1.5 1.5" />
                                            <path d="M12 1 C9.5 5 7 8 7 14 C7 18.5 9.2 21 12 21 C14.8 21 17 18.5 17 14 C17 8 14.5 5 12 1 Z M12 9 C13.5 12 14.5 14 14.5 16 C14.5 18 13.4 19 12 19 C10.6 19 9.5 18 9.5 16 C9.5 14 10.5 12 12 9 Z" fill="url(#flameGradCoinModal)" />
                                        </svg>
                                        {(viewerProfile?.tokenBalance || 0).toLocaleString()} <span style={{ color: '#4ADE80', fontSize: '11px', fontWeight: 'bold' }}>(≈ ${Math.round((viewerProfile?.tokenBalance || 0) * 25).toLocaleString()} GYD)</span>
                                    </p>
                                </div>
                                <button 
                                    type="button" 
                                    onClick={() => setIsVaultOpen(true)}
                                    style={{ background: 'linear-gradient(90deg, #FF4500, #FF8C00)', border: 'none', color: '#FFF', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', boxShadow: '0 0 10px rgba(255,69,0,0.4)' }}
                                >
                                    + GET TOKENS
                                </button>
                            </div>

                            {/* Token Cards Grid */}
                            <div style={{ maxHeight: '240px', overflowY: 'auto', marginBottom: '16px', paddingRight: '4px' }}>
                                {giftTokens.map(token => {
                                    const tokenCost = Math.round(token.price / 25);
                                    const tokenPlatformFee = Math.round(token.price * 0.15);
                                    const tokenActorReceives = Math.round(token.price * 0.85);
                                    return (
                                        <div key={token.id} className={`token-card ${selectedToken.id === token.id ? 'selected' : ''}`} onClick={() => setSelectedToken(token)}>
                                            <div className="token-icon">{token.icon}</div>
                                            <div className="token-info">
                                                <p className="token-name">{token.name}</p>
                                                <p className="token-breakdown">Creator Receives: <strong style={{ color: '#4ADE80', fontWeight: 'bold' }}>{tokenActorReceives.toLocaleString()} GYD</strong> &nbsp;•&nbsp; Fee: {tokenPlatformFee.toLocaleString()} GYD (15%)</p>
                                            </div>
                                            <span className="token-price" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <svg viewBox="0 0 24 28" width="14" height="18" style={{ overflow: 'visible', filter: 'drop-shadow(0 2px 4px rgba(255,69,0,0.8))' }}>
                                                    <defs>
                                                        <linearGradient id={`flameGradCard_${token.id}`} x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#FFD700"/>
                                                            <stop offset="50%" stopColor="#FF4500"/>
                                                            <stop offset="100%" stopColor="#8B0000"/>
                                                        </linearGradient>
                                                    </defs>
                                                    <circle cx="12" cy="17" r="8" fill="#B8860B" stroke="#FFD700" strokeWidth="1.2" />
                                                    <circle cx="12" cy="17" r="6" fill="none" stroke="#FFE55C" strokeWidth="0.8" strokeDasharray="1.5 1.5" />
                                                    <path d="M12 1 C9.5 5 7 8 7 14 C7 18.5 9.2 21 12 21 C14.8 21 17 18.5 17 14 C17 8 14.5 5 12 1 Z M12 9 C13.5 12 14.5 14 14.5 16 C14.5 18 13.4 19 12 19 C10.6 19 9.5 18 9.5 16 C9.5 14 10.5 12 12 9 Z" fill={`url(#flameGradCard_${token.id})`} />
                                                </svg>
                                                {tokenCost.toLocaleString()}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Anonymous Toggle */}
                            <label className="anon-toggle">
                                <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} style={{ accentColor: '#FF4500', width: '16px', height: '16px' }} />
                                <span>Tip anonymously (hide my name from public toasts)</span>
                            </label>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                <button className="submit-btn cancel-btn" style={{ flex: 1 }} onClick={() => setIsOpen(false)} disabled={isSubmitting}>Cancel</button>
                                <button 
                                    className="submit-btn" 
                                    style={{ flex: 2 }} 
                                    onClick={() => {
                                        if (!hasEnoughTokens) {
                                            setIsVaultOpen(true);
                                        } else {
                                            setShowEarningsConfirm(true);
                                        }
                                    }} 
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Processing...' : (!hasEnoughTokens ? 'Get Tokens' : `Send Reward (${requiredTokens} Tokens)`)}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="success-state">
                            <div className="success-check" style={{ margin: '0 auto 16px' }}>✓</div>
                            <h3 style={{ color: '#FFFFFF', fontSize: '24px', fontWeight: 900, margin: '0 0 12px 0', letterSpacing: '0.02em' }}>
                                Reward Sent!
                            </h3>
                            <p style={{ color: '#AAA', fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                                Your <strong style={{color: '#FFD700'}}>{selectedToken?.name || 'Gift'}</strong> has been securely transferred to the creator.
                            </p>
                        </div>
                    )}
                </div>

                {/* EARNINGS CONFIRMATION MODAL OVERLAY */}
                {showEarningsConfirm && (
                    <div className="global-gift-overlay" style={{ zIndex: 16000, background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(15px)' }} onClick={() => setShowEarningsConfirm(false)}>
                        <div className="global-gift-modal" style={{ maxWidth: '360px', border: '1px solid #FF4500', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                            <p style={{ color: '#FF4500', fontSize: '18px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' }}>Authorize Transfer</p>
                            <p style={{ color: '#FFF', fontSize: '14px', lineHeight: '1.5', marginBottom: '24px' }}>
                                Are you sure you want to deduct <strong style={{ color: '#FFD700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    <svg viewBox="0 0 24 28" width="16" height="20" style={{ overflow: 'visible', filter: 'drop-shadow(0 2px 4px rgba(255,69,0,0.8))' }}>
                                        <defs>
                                            <linearGradient id="flameGradCoinConfirm" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#FFD700"/>
                                                <stop offset="50%" stopColor="#FF4500"/>
                                                <stop offset="100%" stopColor="#8B0000"/>
                                            </linearGradient>
                                        </defs>
                                        <circle cx="12" cy="17" r="8" fill="#B8860B" stroke="#FFD700" strokeWidth="1.2" />
                                        <circle cx="12" cy="17" r="6" fill="none" stroke="#FFE55C" strokeWidth="0.8" strokeDasharray="1.5 1.5" />
                                        <path d="M12 1 C9.5 5 7 8 7 14 C7 18.5 9.2 21 12 21 C14.8 21 17 18.5 17 14 C17 8 14.5 5 12 1 Z M12 9 C13.5 12 14.5 14 14.5 16 C14.5 18 13.4 19 12 19 C10.6 19 9.5 18 9.5 16 C9.5 14 10.5 12 12 9 Z" fill="url(#flameGradCoinConfirm)" />
                                    </svg>
                                    {requiredTokens} Tokens
                                </strong> (≈ ${selectedToken?.price.toLocaleString()} GYD) from your wallet to send this reward?
                            </p>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button className="submit-btn cancel-btn" onClick={() => setShowEarningsConfirm(false)} style={{ flex: 1, margin: 0 }}>Cancel</button>
                                <button 
                                    className="submit-btn" 
                                    style={{ flex: 1.5, margin: 0, background: 'linear-gradient(135deg, #FF4500 0%, #FF8C00 100%)' }}
                                    onClick={handleSendCatalogTip}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Processing..." : "Confirm"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Global Token Vault Overlay (Layered on top of Tipping Modal) */}
            {isVaultOpen && (
                <div style={{ position: 'relative', zIndex: 25000 }}>
                    <RoastTokenVault 
                        isOpen={isVaultOpen} 
                        onClose={() => setIsVaultOpen(false)} 
                        currentUser={{ uid: viewerProfile?.id || viewerProfile?.uid }}
                        creatorProfile={viewerProfile}
                        showMessage={(msg) => alert(msg)}
                    />
                </div>
            )}
        </>
    );
};

export default GlobalTippingModal;